import { useTranslation } from "react-i18next";

import { Pagination } from "@/components/Pagination";
import { EmptyState, ErrorState, Spinner } from "@/components/ui";
import { formatRelative } from "@/lib/format";
import type { FolderParam, MailMessage } from "./queries";
import { recipientsLabel, senderLabel, useMailMessages } from "./queries";

/**
 * Who the row is about: the sender in Recibidos, the recipients in Enviados and
 * in Borradores. A Sent list that shows the sender shows the seller their own
 * name, forty times.
 */
function counterparty(message: MailMessage, folder: FolderParam): string {
  return folder === "recibidos" ? senderLabel(message) : recipientsLabel(message.to);
}

function Row({
  message,
  folder,
  isSelected,
  onSelect,
}: {
  message: MailMessage;
  folder: FolderParam;
  isSelected: boolean;
  onSelect: (id: number) => void;
}) {
  const { t } = useTranslation();
  const unread = folder === "recibidos" && !message.is_read;

  return (
    <li>
      <button
        type="button"
        onClick={() => onSelect(message.id)}
        aria-current={isSelected}
        className={`flex w-full flex-col gap-1 border-b border-rule px-3 py-3 text-left hover:bg-mute-200 ${
          isSelected ? "bg-mute-200" : ""
        }`}
      >
        <div className="flex items-baseline gap-2">
          <span
            className={`min-w-0 flex-1 truncate text-sm ${unread ? "font-bold" : ""}`}
          >
            {counterparty(message, folder) || t("mail.noRecipients")}
          </span>
          {/* A queued or failed send is the seller's business right now; a
              delivered one needs no chip at all. */}
          {message.status === "failed" ? (
            <span className="tag tag-accent shrink-0">{t("mail.failed")}</span>
          ) : null}
          {message.status === "queued" ? (
            <span className="tag tag-outline shrink-0">{t("mail.queued")}</span>
          ) : null}
        </div>
        <span className={`truncate text-sm ${unread ? "font-semibold" : ""}`}>
          {message.subject || t("mail.noSubject")}
        </span>
        <div className="flex items-baseline gap-2 text-xs text-mute-600">
          <span className="min-w-0 flex-1 truncate">{message.summary}</span>
          {message.has_attachments ? (
            <span aria-label={t("mail.hasAttachment")}>📎</span>
          ) : null}
          <span className="shrink-0">{formatRelative(message.occurred_at)}</span>
        </div>
      </button>
    </li>
  );
}

export function MessageList({
  folder,
  search,
  page,
  selectedId,
  onSelect,
  onPageChange,
}: {
  folder: FolderParam;
  search: string;
  page: number;
  selectedId: number | null;
  onSelect: (id: number) => void;
  onPageChange: (page: number) => void;
}) {
  const { t } = useTranslation();
  const query = useMailMessages({ folder, search, page });
  const messages = query.data?.results ?? [];

  if (query.isPending) return <Spinner label={t("mail.loading")} />;
  if (query.isError) {
    return (
      <ErrorState
        message={
          query.error instanceof Error ? query.error.message : t("mail.loadFailed")
        }
        onRetry={() => void query.refetch()}
      />
    );
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {messages.length === 0 ? (
        <EmptyState>{search ? t("mail.noMatches") : t("mail.empty")}</EmptyState>
      ) : (
        <ul className="min-h-0 flex-1 overflow-y-auto">
          {messages.map((message) => (
            <Row
              key={message.id}
              message={message}
              folder={folder}
              isSelected={message.id === selectedId}
              onSelect={onSelect}
            />
          ))}
        </ul>
      )}
      <Pagination
        page={page}
        count={query.data?.count ?? 0}
        shown={messages.length}
        onChange={onPageChange}
        isFetching={query.isFetching}
      />
    </div>
  );
}
